/**
 * VoucherMigration.jsx
 * ==========================================================================
 * Admin tool untuk memindahkan voucher massal (batch) antara cabang & agent.
 * Flow:
 *   1) User isi filter Dari (cabang & agent asal — bisa "null" untuk Pusat/Tanpa)
 *   2) User isi paket contains (opsional)
 *   3) User pilih target cabang & agent
 *   4) Klik Preview → tampil count + sample 10 voucher
 *   5) Klik Konfirmasi Migrate → auto-backup + UPDATE
 *   6) History migrasi + tombol Rollback per entry
 * 2026-07-15
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, ArrowRight, History, RotateCcw, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoucherMigration() {
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);

  const [fromBranch, setFromBranch] = useState('null');   // 'null' | 'all' | numeric
  const [fromAgent, setFromAgent] = useState('all');
  const [packageContains, setPackageContains] = useState('');
  const [targetBranch, setTargetBranch] = useState('');
  const [targetAgent, setTargetAgent] = useState('none');

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadMasters = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([
        axios.get(`${API_URL}/api/branches`, auth()),
        axios.get(`${API_URL}/api/voucher-agents?limit=500`, auth()),
      ]);
      const branchList = b.data?.data || b.data || [];
      setBranches(Array.isArray(branchList) ? branchList : []);
      const agentList = a.data?.data || a.data || [];
      setAgents(Array.isArray(agentList) ? agentList : []);
    } catch (e) {
      toast.error('Gagal memuat data cabang/agen');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/vouchers/bulk-migrate/history?limit=15`, auth());
      setHistory(r.data?.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadMasters(); loadHistory(); }, [loadMasters, loadHistory]);

  const handlePreview = async () => {
    if (!targetBranch) {
      toast.error('Pilih cabang tujuan');
      return;
    }
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const body = {
        from_branch_id: fromBranch,
        from_agent_id: fromAgent,
        package_contains: packageContains || null,
        target_branch_id: parseInt(targetBranch, 10),
        target_agent_id: (targetAgent && targetAgent !== 'none') ? parseInt(targetAgent, 10) : null,
      };
      const r = await axios.post(`${API_URL}/api/vouchers/bulk-migrate/preview`, body, auth());
      setPreviewResult(r.data);
      if ((r.data?.total || 0) === 0) toast.warning('Tidak ada voucher yang cocok dengan filter ini');
      else toast.success(`Ditemukan ${r.data.total} voucher siap dipindah`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview gagal');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const body = {
        from_branch_id: fromBranch,
        from_agent_id: fromAgent,
        package_contains: packageContains || null,
        target_branch_id: parseInt(targetBranch, 10),
        target_agent_id: (targetAgent && targetAgent !== 'none') ? parseInt(targetAgent, 10) : null,
        confirm: true,
      };
      const r = await axios.post(`${API_URL}/api/vouchers/bulk-migrate/execute`, body, auth());
      toast.success(r.data?.message || `Berhasil pindah ${r.data?.migrated} voucher`);
      setConfirmOpen(false);
      setPreviewResult(null);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migrasi gagal');
    } finally {
      setExecuting(false);
    }
  };

  const handleRollback = async (backupId) => {
    if (!window.confirm(`Rollback backup #${backupId}? Semua voucher akan dikembalikan ke cabang/agen sebelumnya.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/vouchers/bulk-migrate/rollback/${backupId}`, {}, auth());
      toast.success(`Rollback sukses: ${r.data?.rolled_back} voucher dikembalikan`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback gagal');
    }
  };

  const targetBranchAgents = agents.filter(a => !targetBranch || String(a.branch_id) === String(targetBranch));

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="voucher-migration-page">
      <div>
        <h1 className="text-2xl font-bold">Migrasi Voucher Massal</h1>
        <p className="text-sm text-slate-500">Pindahkan voucher batch dari satu cabang/agen ke cabang/agen lain. Otomatis backup sebelum update.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Konfigurasi Migrasi</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* FROM */}
            <div className="space-y-3 border-r-0 md:border-r pr-0 md:pr-6">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Dari (Sumber)</div>
              <div>
                <Label>Cabang Asal</Label>
                <Select value={fromBranch} onValueChange={setFromBranch}>
                  <SelectTrigger data-testid="from-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">— Semua Cabang —</SelectItem>
                    <SelectItem value="null">Pusat / Tanpa Cabang</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agen Asal</Label>
                <Select value={fromAgent} onValueChange={setFromAgent}>
                  <SelectTrigger data-testid="from-agent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">— Semua Agen —</SelectItem>
                    <SelectItem value="null">Tanpa Agen</SelectItem>
                    {agents.map(a => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Filter Paket Berisi (opsional)</Label>
                <Input
                  value={packageContains}
                  onChange={(e) => setPackageContains(e.target.value)}
                  placeholder="Contoh: MAYAK, 12 JAM, 1 HARI"
                  data-testid="package-contains"
                />
              </div>
            </div>

            {/* TO */}
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Ke (Tujuan)</div>
              <div>
                <Label>Cabang Tujuan *</Label>
                <Select value={targetBranch} onValueChange={(v) => { setTargetBranch(v); setTargetAgent('none'); }}>
                  <SelectTrigger data-testid="target-branch"><SelectValue placeholder="Pilih cabang..." /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agen Tujuan (opsional)</Label>
                <Select value={targetAgent} onValueChange={setTargetAgent}>
                  <SelectTrigger data-testid="target-agent"><SelectValue placeholder="Tidak ubah agen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak Ubah Agen —</SelectItem>
                    {targetBranchAgents.map(a => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {targetBranch && targetBranchAgents.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Belum ada agen di cabang ini.</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={handlePreview} disabled={previewLoading || !targetBranch} data-testid="btn-preview">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Preview Migrasi
            </Button>
            <Button
              variant="default"
              disabled={!previewResult || previewResult.total === 0}
              onClick={() => setConfirmOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-migrate"
            >
              Konfirmasi & Migrate {previewResult?.total > 0 ? `(${previewResult.total})` : ''}
            </Button>
          </div>

          {previewResult && (
            <div className="mt-4 border rounded-lg p-4 bg-slate-50 dark:bg-slate-900" data-testid="preview-result">
              <div className="text-sm mb-2">
                <span className="font-semibold text-lg">{previewResult.total}</span> voucher akan dipindah ke{' '}
                <span className="font-semibold">{previewResult.target_branch?.name}</span>
                {previewResult.target_agent && <> / agen <span className="font-semibold">{previewResult.target_agent.name}</span></>}
              </div>
              {previewResult.sample?.length > 0 && (
                <div className="text-xs mt-3">
                  <div className="font-semibold mb-1">Sample voucher (10 pertama):</div>
                  <div className="max-h-48 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="text-left p-1">Kode</th>
                          <th className="text-left p-1">Paket</th>
                          <th className="text-left p-1">Cabang</th>
                          <th className="text-left p-1">Agen</th>
                          <th className="text-left p-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewResult.sample.map(v => (
                          <tr key={v.id} className="border-t border-slate-200 dark:border-slate-700">
                            <td className="p-1 font-mono">{v.code}</td>
                            <td className="p-1">{v.package_name}</td>
                            <td className="p-1">{v.branch_name || '—'}</td>
                            <td className="p-1">{v.agent_name || '—'}</td>
                            <td className="p-1">{v.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Riwayat Migrasi
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500">Belum ada riwayat migrasi.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-800">
                  <tr>
                    <th className="text-left p-2">ID</th>
                    <th className="text-left p-2">Waktu</th>
                    <th className="text-left p-2">Oleh</th>
                    <th className="text-left p-2">Target Cabang</th>
                    <th className="text-left p-2">Target Agent</th>
                    <th className="text-right p-2">Rows</th>
                    <th className="text-left p-2">Filter</th>
                    <th className="text-center p-2">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const rolledBack = (h.notes || '').includes('rolled_back_at=');
                    return (
                      <tr key={h.id} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="p-2 font-mono">#{h.id}</td>
                        <td className="p-2">{h.performed_at}</td>
                        <td className="p-2">{h.performed_by}</td>
                        <td className="p-2">{h.target_branch_id}</td>
                        <td className="p-2">{h.target_agent_id || '—'}</td>
                        <td className="p-2 text-right font-semibold">{h.row_count}</td>
                        <td className="p-2 text-xs font-mono max-w-xs truncate" title={h.filter_json}>{h.filter_json}</td>
                        <td className="p-2 text-center">
                          {rolledBack ? (
                            <span className="text-xs text-slate-400">rolled back</span>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => handleRollback(h.id)} data-testid={`rollback-${h.id}`}>
                              <RotateCcw className="w-3 h-3 mr-1" /> Rollback
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" /> Konfirmasi Migrasi Voucher
            </DialogTitle>
            <DialogDescription>
              Anda akan memindahkan <span className="font-bold text-red-600">{previewResult?.total}</span> voucher ke cabang{' '}
              <span className="font-bold">{previewResult?.target_branch?.name}</span>
              {previewResult?.target_agent && <> dan agen <span className="font-bold">{previewResult.target_agent.name}</span></>}.
              <br /><br />
              Backup otomatis akan disimpan sehingga rollback dimungkinkan. Tetap lanjutkan?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={executing}>Batal</Button>
            <Button onClick={handleExecute} disabled={executing} className="bg-red-600 hover:bg-red-700" data-testid="confirm-execute">
              {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Ya, Migrate Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
